import Button from '@atoms/Button/Button';
import LoadingSpinner from '@atoms/LoadingSpinner/LoadingSpinner';
import Text from '@atoms/Text/Text';
import FormStateEnum from '@models/enums/FormStateEnum';
import SizeEnum from '@models/enums/SizeEnum';
import { IContactFormWrapperProps } from '@models/IFormWrapper';
import Form from '@molecules/Form/Form';
import { PropsWithChildren, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';

const FormAlert = ({ title, body }: { title: string; body: string }) => (
  <div className="tw-flex tw-h-full tw-flex-col tw-justify-center tw-text-center">
    <Text as="h3" variant="heading-24-light" className="tw-mb-2">
      {title}
    </Text>
    <Text as="p" className="tw-text-grey-80">
      {body}
    </Text>
  </div>
);

const FormSubmit = ({
  validationMessage,
  submitLabel,
}: {
  validationMessage: string;
  submitLabel: string;
}) => {
  const {
    formState: { isSubmitted, isValid },
  } = useFormContext();
  return (
    <div>
      {isSubmitted && !isValid && (
        <Text
          variant="label-12"
          className="tw-mb-6 tw-block tw-text-red"
          role="alert"
        >
          {validationMessage}
        </Text>
      )}
      <div>
        <Button type="submit" label={submitLabel} />
      </div>
    </div>
  );
};

const ContactFormWrapper = ({
  text,
  step,
  children,
  registerOptions,
  onSubmit,
}: PropsWithChildren<IContactFormWrapperProps>) => {
  const formContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (step !== FormStateEnum.Idle && formContainerRef.current) {
      formContainerRef.current.scrollIntoView?.();
    }
  }, [step]);

  return (
    <div className="tw-scroll-mt-20 tw-bg-grey-10" ref={formContainerRef}>
      <div className="b2b-container tw-grid tw-grid-cols-2 tw-gap-12 tw-py-16 md:tw-py-20 ">
        <div className="tw-col-span-2 xl:tw-col-span-1">
          <Text
            variant="ds-headline-2"
            className="tw-mb-4 tw-text-black"
            as="h2"
          >
            {text.title}
          </Text>
          <Text
            as="div"
            className="tw-rich-text tw-text-grey-80"
            dangerouslySetInnerHTML={{ __html: text.subtitle }}
          />
        </div>
        <div className="tw-relative tw-col-span-2 tw-min-h-80 xl:tw-col-span-1 xl:tw-min-h-[500px]">
          {step === FormStateEnum.Idle && (
            <Form
              formSubmit={onSubmit}
              options={registerOptions}
              className="tw-col-span-2 tw-grid tw-grid-cols-2 tw-gap-x-8 tw-gap-y-6 xl:tw-col-span-1"
            >
              {children}
              <FormSubmit
                submitLabel={text.submitLabel}
                validationMessage={text.validationSummary}
              />
            </Form>
          )}
          {step === FormStateEnum.Success && (
            <FormAlert
              title={text.confirmationTitle}
              body={text.confirmationBody}
            />
          )}
          {step === FormStateEnum.Error && (
            <FormAlert title={text.errorTitle} body={text.errorBody} />
          )}
          {step === FormStateEnum.Loading && (
            <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center">
              <LoadingSpinner
                loadingLabel={text.loadingLabel}
                size={SizeEnum.Medium}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactFormWrapper;
